import React, { FC } from 'react';
import { ConnectionModel } from '../../connection.model';
import './ecm-inline-actions.scss';
import EditIcon from '@svg/pencil.svg?react';
import PullPolicyIcon from '@svg/pull-policy-icon.svg?react';
import CancelSmartIcon from '@svg/cancel-smart.svg?react';
import AssignPolicyIcon from '@svg/assign-policy.svg?react';
import ViewIcon from '@svg/view.svg?react';
import { IconWithTooltip } from '../connection-policy-info-cell/icon-with-tooltip';
import { ModalState } from '../ecm-modals';
import { ECMActionType, VersionLabel } from '../../../../../utils/enums';
import { useStores } from '../../../../store';
import { EventType } from '../../../../../dtos/analytics.dto';
import { observer } from 'mobx-react-lite';

const EcmInlineActions: FC<{
    connection: ConnectionModel,
    setModalState: React.Dispatch<React.SetStateAction<ModalState>>
}> = observer(({connection, setModalState}) => {

    const { customerStore } = useStores();

    // Only show edit icon for bronze (standard) connections
    const isBronze = connection.serviceLevel === 'BRONZE';

    const handleEditStandardNameClick = () => {
        customerStore.sendGeneralEvent(EventType.LinkClicked, { button: 'editStandardConnectionName', location: 'ECM' });
        setModalState({ type: 'editStandardName', connection });
    };

    if (isBronze) {
        return (
            <div className="actions">
                <button type="button" className="action-icon-wrapper" onClick={handleEditStandardNameClick}>
                    <IconWithTooltip
                        title="Edit connection name"
                        tooltipClassName="edit-tooltip"
                        iconClassName="action-icon edit"
                        IconComponent={EditIcon}
                    />
                </button>
            </div>
        );
    }

    // Restore full inline actions for non-bronze connections
    const isAssignDisabled = connection.policyStatus === VersionLabel.Pending ||
        !connection.isAvailable ||
        connection.isSmartDraftInProgress ||
        connection.isSourceSchedule ||
        (connection.isTargetSchedule && connection.isUpcomingSchedule);

    const handleNewPolicyClick = () => {
        customerStore.sendGeneralEvent(EventType.LinkClicked, { button: 'Newpolicy', number_of_sid: 1 });
        setModalState({type: 'policyActions', connection});
    };

    const handlePolicySummaryClick = () => {
        customerStore.sendGeneralEvent(
            EventType.LinkClicked,
            {
                button: 'policySummary'
            }
        );
        setModalState({type: 'policySummary', connection});
    };

    const handleCancelSmartClick = () => {
        customerStore.sendGeneralEvent(EventType.LinkClicked, {
            button: 'cancelSmartProtection',
            location: 'ECM'
        });

        setModalState({type: 'cancelSmart', connection});
    };

    const handleEditConnectionClick = () => {
        customerStore.sendGeneralEvent(EventType.LinkClicked, { button: 'editConnectionDetails', location: 'ECM' });
        setModalState({type: 'editConnection', connection});
    };

    const handleAssignPolicyClick = () => {
        customerStore.sendGeneralEvent(EventType.LinkClicked, { button: 'assignPolicy', location: 'ECM' });
        setModalState({type: 'policyActions', connection, actionType: ECMActionType.PullFromExisting});
    };

    return (
        <div className="actions">
            {connection.isInitial || connection.isSmartEligible ? (
                <button type="button" className="action-icon-wrapper" disabled={isAssignDisabled}
                        onClick={handleNewPolicyClick}>
                    <IconWithTooltip
                        title="New policy"
                        tooltipClassName="assign-tooltip"
                        iconClassName={`action-icon assign ${isAssignDisabled ? 'disabled' : ''}`}
                        IconComponent={AssignPolicyIcon}
                    />
                </button>
            ) : (
                <button type="button" className="action-icon-wrapper" disabled={isAssignDisabled}
                        onClick={handleAssignPolicyClick}>
                    <IconWithTooltip
                        title="Assign policy"
                        tooltipClassName="pull-tooltip"
                        iconClassName={`action-item pull ${isAssignDisabled ? 'disabled' : ''}`}
                        IconComponent={PullPolicyIcon}
                    />
                </button>
            )}
            <button type="button" className="action-icon-wrapper" onClick={handlePolicySummaryClick}>
                <IconWithTooltip
                    title="View policy summary"
                    tooltipClassName="view-tooltip"
                    iconClassName="action-icon view"
                    IconComponent={ViewIcon}
                />
            </button>
            <button type="button" className="action-icon-wrapper" onClick={handleEditConnectionClick}>
                <IconWithTooltip
                    title="Edit connection details"
                    tooltipClassName="edit-tooltip"
                    iconClassName="action-icon edit"
                    IconComponent={EditIcon}
                />
            </button>
            {(connection.isInAnalysis && connection.analysisDaysLeft > 0) && (
                <button type="button" className="action-icon-wrapper" onClick={handleCancelSmartClick}>
                    <IconWithTooltip
                        title="Cancel smart protection"
                        tooltipClassName="cancel-tooltip"
                        iconClassName="action-icon cancel"
                        IconComponent={CancelSmartIcon}
                    />
                </button>
            )}
        </div>
    );
});

export default EcmInlineActions;
